import type React from 'react';
import { Controller } from 'react-hook-form';
import { Form, Select } from 'antd';
import type { FieldValues } from 'react-hook-form';
import type { SelectProps } from 'antd';
import type { RHFBaseProps, CommonChrome } from './types';
import { DEFAULT_FORM_LAYOUT } from './constants';

export type OptionValue = string | number;

export type ControlledSelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TOptionValue extends OptionValue = OptionValue,
> = RHFBaseProps<TFieldValues> &
  CommonChrome &
  Omit<
    SelectProps<
      TOptionValue | TOptionValue[],
      { label: React.ReactNode; value: TOptionValue }
    >,
    'value' | 'onChange'
  >;

export function ControlledSelect<
  TFieldValues extends FieldValues = FieldValues,
  TOptionValue extends OptionValue = OptionValue,
>({
  control,
  name,
  label,
  required,
  disabled,
  help,
  options,
  mode,
  layout,
  fieldClassName,
  suffixIcon,
  loading,
  ...rest
}: ControlledSelectProps<TFieldValues, TOptionValue>) {
  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { value, onChange }, fieldState }) => (
        <Form.Item
          label={label}
          required={required}
          validateStatus={fieldState.error ? 'error' : undefined}
          help={fieldState.error?.message ?? help}
          layout={layout ?? DEFAULT_FORM_LAYOUT}
          className={fieldClassName}
        >
          <Select<
            TOptionValue | TOptionValue[],
            { label: React.ReactNode; value: TOptionValue }
          >
            {...rest}
            suffixIcon={loading ? undefined : suffixIcon}
            loading={loading}
            options={options}
            mode={mode}
            value={value ?? undefined}
            onChange={onChange}
            disabled={disabled || loading}
            style={{ width: rest.style?.width ?? '100%' }}
          />
        </Form.Item>
      )}
    />
  );
}
